import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { useCreateFAQ, useUpdateFAQ, useProducts, usePrograms, usePackages, usePartners } from '@/hooks/usePartnerships';
import type { FAQ } from '../../../../shared/types/partnerships';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  faq?: FAQ;
}

const STATUSES = ['PUBLISHED', 'DRAFT', 'ARCHIVED'] as const;
// Radix Select (>=2.x) lanza un error si un <SelectItem> tiene value="" y eso tira
// abajo toda la página (no hay ErrorBoundary). La opción "ninguno" usa este
// centinela y se traduce a '' al guardar en el form.
const NONE = '__none__';

interface FormData {
  question: string; answer: string; category: string;
  order: string; relatedProductId: string;
  relatedPackageId: string; relatedProgramId: string;
  relatedPartnerId: string; status: string;
}

export function FAQFormDialog({ open, onOpenChange, partnerId, faq }: Props) {
  const { t } = useTranslation();
  const isEditing = !!faq;
  const create = useCreateFAQ();
  const update = useUpdateFAQ();
  const { data: products = [] } = useProducts(partnerId);
  const { data: packages = [] } = usePackages(partnerId);
  const { data: programs = [] } = usePrograms(partnerId);
  const { data: partners = [] } = usePartners();

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>();
  const statusValue = watch('status');
  const relatedProductValue = watch('relatedProductId');
  const relatedPackageValue = watch('relatedPackageId');
  const relatedProgramValue = watch('relatedProgramId');
  const relatedPartnerValue = watch('relatedPartnerId');

  useEffect(() => {
    if (open) {
      reset({
        question: faq?.question || '', answer: faq?.answer || '',
        category: faq?.category || '', order: faq?.order?.toString() || '0',
        relatedProductId: faq?.relatedProductId || '',
        relatedPackageId: faq?.relatedPackageId || '',
        relatedProgramId: faq?.relatedProgramId || '',
        relatedPartnerId: faq?.relatedPartnerId || '',
        status: faq?.status || 'PUBLISHED',
      });
    }
  }, [open, faq, reset]);

  const onSubmit = async (data: FormData) => {
    const payload = {
      question: data.question, answer: data.answer,
      category: data.category, order: Number(data.order) || 0,
      relatedProductId: data.relatedProductId || undefined,
      relatedPackageId: data.relatedPackageId || undefined,
      relatedProgramId: data.relatedProgramId || undefined,
      relatedPartnerId: data.relatedPartnerId || undefined,
      status: data.status as any,
    };

    if (isEditing) {
      await update.mutateAsync({ faqId: faq!.faqId, ...payload, expectedUpdatedAt: faq!.updatedAt });
    } else {
      await create.mutateAsync({ partnerId, ...payload });
    }
    onOpenChange(false);
  };

  const isBusy = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('partnerships.faq_form.title_edit') : t('partnerships.faq_form.title_create')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          <div className="space-y-1.5">
            <Label>{t('partnerships.faq_form.question_label')}</Label>
            <Input {...register('question', { required: true })} placeholder={t('partnerships.faq_form.question_placeholder')} />
            {errors.question && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.faq_form.answer_label')}</Label>
            <Textarea {...register('answer', { required: true })} rows={5} />
            {errors.answer && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.faq_form.category_label')}</Label>
              <Input {...register('category', { required: true })} placeholder={t('partnerships.faq_form.category_placeholder')} />
              {errors.category && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.faq_form.order_label')}</Label>
              <Input {...register('order')} type="number" min={0} />
            </div>
          </div>

          {products.length > 0 && (
            <div className="space-y-1.5">
              <Label>{t('partnerships.faq_form.related_product_label')}</Label>
              <Select value={relatedProductValue || NONE} onValueChange={v => setValue('relatedProductId', v === NONE ? '' : v)}>
                <SelectTrigger><SelectValue placeholder={t('partnerships.faq_form.no_related_product')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('partnerships.faq_form.no_related_product')}</SelectItem>
                  {products.map(p => <SelectItem key={p.productId} value={p.productId}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {packages.length > 0 && (
            <div className="space-y-1.5">
              <Label>{t('partnerships.faq_form.related_package_label')}</Label>
              <Select value={relatedPackageValue || NONE} onValueChange={v => setValue('relatedPackageId', v === NONE ? '' : v)}>
                <SelectTrigger><SelectValue placeholder={t('partnerships.faq_form.no_related_package')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('partnerships.faq_form.no_related_package')}</SelectItem>
                  {packages.map(p => <SelectItem key={p.packageId} value={p.packageId}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {programs.length > 0 && (
            <div className="space-y-1.5">
              <Label>{t('partnerships.faq_form.related_program_label')}</Label>
              <Select value={relatedProgramValue || NONE} onValueChange={v => setValue('relatedProgramId', v === NONE ? '' : v)}>
                <SelectTrigger><SelectValue placeholder={t('partnerships.faq_form.no_related_program')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('partnerships.faq_form.no_related_program')}</SelectItem>
                  {programs.map(p => <SelectItem key={p.programId} value={p.programId}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {partners.length > 0 && (
            <div className="space-y-1.5">
              <Label>{t('partnerships.faq_form.related_partner_label')}</Label>
              <Select value={relatedPartnerValue || NONE} onValueChange={v => setValue('relatedPartnerId', v === NONE ? '' : v)}>
                <SelectTrigger><SelectValue placeholder={t('partnerships.faq_form.no_related_partner')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('partnerships.faq_form.no_related_partner')}</SelectItem>
                  {partners.map(p => <SelectItem key={p.partnerId} value={p.partnerId}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.status_label')}</Label>
            <Select value={statusValue} onValueChange={v => setValue('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel')}</Button>
            <Button type="submit" disabled={isBusy}>
              {isBusy ? t('partnerships.common.saving') : isEditing ? t('partnerships.common.save_changes') : t('partnerships.faq_form.create_button')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
